import PropTypes from 'prop-types'
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react'
import { useNotify, useRecordContext } from 'react-admin'
import { checkAdjustmentStatus } from '../RestUtils'

const AdjustmentStatusContext = createContext()

export const AdjustmentStatusProvider = ({ children }) => {
  const record = useRecordContext()
  const notify = useNotify()
  const [missingSections, setMissingSections] = useState({})
  const [allowGetOffers, setAllowGetOffers] = useState(false)
  const [convertScrap, setConvertScrap] = useState(null)
  const isLoadingRef = useRef(false)

  const refresh = useCallback(
    async (name) => {
      if (!record?.originId || isLoadingRef.current) return
      isLoadingRef.current = true

      try {
        const response = await checkAdjustmentStatus(record.originId, name)
        const missing = response.missing ?? {}

        setMissingSections(missing)
        setAllowGetOffers(!Object.values(missing).some(Boolean))
        setConvertScrap(response.convert_scrap || null)

        // Allow error messaging after setting other states to block get offers button and update sections
        // Only runs after an adjustment has been made, not on load
        if (
          response.error_msg &&
          name !== undefined &&
          name !== 'Damage Changed'
        ) {
          throw new Error(response.error_msg)
        }
      } catch (error) {
        if (error.message !== 'false') notify(error.message, { type: 'error' })
        setAllowGetOffers(false)
        setConvertScrap(null)
      } finally {
        isLoadingRef.current = false
      }
    },
    [record?.originId, notify]
  )

  useEffect(() => {
    refresh()
  }, [record?.originId, refresh])

  return (
    <AdjustmentStatusContext.Provider
      value={{
        missingSections,
        allowGetOffers,
        setAllowGetOffers,
        convertScrap,
        refresh,
      }}
    >
      {children}
    </AdjustmentStatusContext.Provider>
  )
}

AdjustmentStatusProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useAdjustmentStatusContext = () => {
  const context = useContext(AdjustmentStatusContext)
  if (!context)
    throw new Error(
      'useAdjustmentStatusContext must be used within AdjustmentStatusProvider'
    )
  return context
}
